import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BookkeeperCard from '../components/BookkeeperCard';

const Listing = () => {
  const [bookkeepers, setBookkeepers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [zipInput, setZipInput] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const zipFromQuery = queryParams.get('zipCode');

  // Mock data for demonstration - replace with real API call later
  const mockBookkeepers = [
    {
      _id: '1',
      fullName: 'John Smith',
      email: 'john.smith@email.com',
      phone: '(555) 123-4567',
      address: '123 Main St',
      city: 'New York',
      state: 'NY',
      zipCode: '10001',
      experienceYears: '5',
      hourlyRate: '75',
      bio: 'Certified public accountant with 5 years of experience specializing in small business bookkeeping and tax preparation.',
      qualifications: 'CPA, QuickBooks Certified',
      specialties: 'Small Business, Tax Preparation',
      status: 'approved',
      isExpired: false,
      preferredContact: 'email'
    },
    {
      _id: '2',
      fullName: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      phone: '(555) 987-6543',
      address: '456 Park Ave',
      city: 'New York',
      state: 'NY',
      zipCode: '10001',
      experienceYears: '8',
      hourlyRate: '85',
      bio: 'Experienced bookkeeper with expertise in non-profit organizations and QuickBooks implementation. Committed to helping businesses maintain accurate financial records.',
      qualifications: 'CMA, QuickBooks ProAdvisor',
      specialties: 'Non-Profit, QuickBooks Implementation',
      status: 'approved',
      isExpired: false,
      preferredContact: 'phone'
    },
    {
      _id: '3',
      fullName: 'Mike Davis',
      email: 'mike.davis@email.com',
      phone: '(555) 456-7890',
      address: '789 Broadway',
      city: 'New York',
      state: 'NY',
      zipCode: '10001',
      experienceYears: '3',
      hourlyRate: '65',
      bio: 'Detail-oriented bookkeeper specializing in retail and e-commerce businesses. Strong background in inventory management and financial reporting.',
      qualifications: 'QuickBooks Certified, Excel Expert',
      specialties: 'Retail, E-commerce, Inventory Management',
      status: 'approved',
      isExpired: false,
      preferredContact: 'email'
    }
  ];

  // Function to try real API - defined at the component level
  const tryRealAPI = async (zipCode) => {
    try {
      // const API_BASE = process.env.NODE_ENV === 'development' 
      //   ? 'https://manage.ebookkeepings.com' 
      //   : '';
      // const API_BASE = 'https://manage.ebookkeepings.com';
      const API_BASE = process.env.REACT_APP_API_BASE_URL;
      
      const response = await fetch(`${API_BASE}/api/bookkeepers/search?zipCode=${zipCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (error) {
      // console.log('Real API not available, using mock data');
    }
    return null;
  };

  const fetchBookkeepers = async (zipCode) => {
  setLoading(true);
  setError('');
  
  try {
    // First try the real API
    const apiData = await tryRealAPI(zipCode);
    
    if (apiData && apiData.success) {
      // Use real API data
      // console.log('Using real API data:', apiData);
      setBookkeepers(apiData.data || []);
      
      if (apiData.data.length === 0) {
        setError(`No approved bookkeepers found for zip code ${zipCode}.`);
      }
    } else {
      // Fallback to mock data with simulated delay
      // console.log('Using mock data for demonstration');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const filteredBookkeepers = mockBookkeepers.filter(
        bookkeeper => bookkeeper.zipCode === zipCode
      );
      
      setBookkeepers(filteredBookkeepers);
      
      if (filteredBookkeepers.length === 0) {
        setError(`No approved bookkeepers found for zip code ${zipCode}. Try zip code 10001 for demo data.`);
      } else {
        // Show demo notice when using mock data
        setError('Demo data showing - real search coming soon!');
      }
    }
    
  } catch (error) {
    console.error('Error fetching bookkeepers:', error);
    setError('Error searching for bookkeepers. Please try again.');
    setBookkeepers([]);
  }
  
  setLoading(false);
};

  useEffect(() => {
    if (zipFromQuery) {
      setZipInput(zipFromQuery);
      fetchBookkeepers(zipFromQuery);
    }
  }, [zipFromQuery]);

  const handleZipSearch = (e) => {
    e.preventDefault();
    const cleanZip = zipInput.trim();
    
    if (!cleanZip) {
      setError('Please enter a zip code');
      return;
    }
    
    if (!/^\d{5}$/.test(cleanZip)) {
      setError('Please enter a valid 5-digit zip code');
      return;
    }
    
    setError('');
    navigate(`/listing?zipCode=${cleanZip}`);
  };

  const getResultsMessage = () => {
    if (bookkeepers.length === 0) return '';
    
    const count = bookkeepers.length;
    return `${count} professional bookkeeper${count !== 1 ? 's' : ''} found near ${zipFromQuery}`;
  };

  return (
    <div className="page listing">
      <div className="container">
        <div className="hero-section">
          <h1>Find Local Bookkeepers</h1>
          <p className="hero-subtitle">
            Search for professional, approved bookkeepers in your area
          </p>
        </div>
        
        {/* Zip Code Search Form */}
        <div className="zip-search-form">
          <form onSubmit={handleZipSearch}>
            <div className="form-group">
              <label htmlFor="zipCode">Enter Your Zip Code</label>
              <div className="form-row">
                <input
                  type="text"
                  id="zipCode"
                  value={zipInput}
                  onChange={(e) => setZipInput(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="e.g., 10001"
                  className="zip-input"
                  required
                />
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Searching...' : 'Search Bookkeepers'}
                </button>
              </div>
              <small style={{display: 'block', marginTop: '5px', color: '#666'}}>
                Try zip codes: 10001, 90210, 60601, 75201
              </small>
            </div>
          </form>
        </div>
        
        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p>Searching for approved bookkeepers in your area...</p>
          </div>
        )}
        
        {error && (
          <div className="error-message">
            <strong>Search Result:</strong> {error}
          </div>
        )}
        
        {!loading && !error && zipFromQuery && (
          <div className="results-section">
            <div className="results-header">
              <h2>{getResultsMessage()}</h2>
              {bookkeepers.length > 0 && (
                <p className="results-subtitle">
                  Showing approved and active bookkeepers only
                </p>
              )}
            </div>
            
            {bookkeepers.length === 0 ? (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No Bookkeepers Found</h3>
                <p>We couldn't find any approved bookkeepers for zip code <strong>{zipFromQuery}</strong>.</p>
                <div className="suggestions">
                  <p>Please try:</p>
                  <ul>
                    <li>Searching with zip code <strong>10001</strong> (has demo data)</li>
                    <li>Checking back later as new bookkeepers join daily</li>
                    <li>Expanding your search to nearby areas</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="bookkeepers-list">
                {bookkeepers.map((bookkeeper) => (
                  <BookkeeperCard 
                    key={bookkeeper._id} 
                    bookkeeper={bookkeeper} 
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        
      </div>
    </div>
  );
};

export default Listing;